import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { useFieldArray, Controller, Control } from "react-hook-form";
import { Trash2, PlusCircle, Quote } from "lucide-react";
import { DndUpload } from "@ui/internal/components/formelement";
import { FormLabel } from "@ui/internal/components/label";
import TextEditor from "@ui/external/components/texteditor";

interface TestimonialContainerProps {
  control: Control<any>;
}

const TestimonialContainer = ({ control }: TestimonialContainerProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "student_testimonial",
  });

  return (
    <div className="w-full space-y-6 mt-10">
      <div className="space-y-4">
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="p-5 rounded-md border bg-white shadow-sm relative group animate-in fade-in slide-in-from-top-2"
          >
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="icon"
                type="button"
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={() => remove(index)}
              >
                <Trash2 size={18} />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <Controller
                  control={control}
                  name={`student_testimonial.${index}.name`}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <FormLabel label="Student Name" />
                      <Input {...field} placeholder="Enter student name" />
                      {error && (
                        <span className="text-xs text-red-500">
                          {error.message}
                        </span>
                      )}
                    </div>
                  )}
                />

                <Controller
                  control={control}
                  name={`student_testimonial.${index}.role`}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <FormLabel label="Role / Designation" />
                      <Input
                        {...field}
                        placeholder="e.g. UI/UX Student, Batch 13"
                      />
                      {error && (
                        <span className="text-xs text-red-500">
                          {error.message}
                        </span>
                      )}
                    </div>
                  )}
                />

                <Controller
                  control={control}
                  name={`student_testimonial.${index}.embed_link`}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <FormLabel label="Instagram Link" />
                      <Input
                        {...field}
                        placeholder="https://www.instagram.com/reel/..."
                      />
                      {error && (
                        <span className="text-xs text-red-500">
                          {error.message}
                        </span>
                      )}
                    </div>
                  )}
                />
              </div>

              <div className="space-y-1">
                <FormLabel label="Student Image" />
                <Controller
                  control={control}
                  name={`student_testimonial.${index}.image`}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <div>
                      <DndUpload
                        type="image"
                        onChange={onChange}
                        value={value}
                      />
                      {error && (
                        <span className="text-xs text-red-500">
                          {error.message}
                        </span>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="mt-4 space-y-1">
              <FormLabel label="Testimonial Description" />
              <Controller
                control={control}
                name={`student_testimonial.${index}.description`}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <div>
                    <TextEditor initialValue={value} onChange={onChange} />
                    {error && (
                      <span className="text-xs text-red-500">
                        {error.message}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-center pt-4">
        <Button
          type="button"
          variant="outline"
          className="flex items-center gap-2 border-[#1DA077] text-[#1DA077] hover:bg-[#1DA077] hover:text-white transition-all"
          onClick={() =>
            append({
              name: "",
              role: "",
              description: "",
              image: "",
              embed_link: "",
            })
          }
        >
          <PlusCircle size={18} />
          Add Testimonial
        </Button>
      </div>
    </div>
  );
};

export default TestimonialContainer;
